import React, { useState, useEffect } from 'react';
import SideNavbar from "../../components/Navbar/SideNavbar";
import Header from "../../components/Header/Header";
import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';

interface Prompt {
  name: string;
  content?: string;
  updated_by: string;
  created_by: string;
  created_at?: string;
  updated_at?: string;
  is_active: boolean;
  version: number;
}

const PromptManagement: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    content: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  // Fetch all prompts
  const fetchPrompts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(apis.LIST_PROMPTS, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.data && response.data.data && Array.isArray(response.data.data.prompts)) {
        setPrompts(response.data.data.prompts);
      } else if (response.data && Array.isArray(response.data)) {
        setPrompts(response.data);
      } else if (response.data.prompts && Array.isArray(response.data.prompts)) {
        setPrompts(response.data.prompts);
      } else {
        setPrompts([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch prompts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch individual prompt content
  const fetchPromptContent = async (promptName: string) => {
    setContentLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`${apis.GET_PROMPT}${promptName}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.data && response.data.data && response.data.data.content) {
        return response.data.data.content;
      }
      return '';
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch prompt content');
      return '';
    } finally {
      setContentLoading(false);
    }
  };

  // Upsert prompt
  const savePrompt = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post(apis.UPSERT_PROMPT, {
        name: editForm.name,
        content: editForm.content,
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      setSuccess('Prompt saved successfully!');
      setIsEditing(false);
      setEditForm({ name: '', content: '' });
      fetchPrompts(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  // Handle prompt selection
  const handlePromptSelect = async (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setIsEditing(false);
    setError('');
    setSuccess('');

    // Fetch the actual content of the prompt
    const content = await fetchPromptContent(prompt.name);
    setSelectedPrompt({ ...prompt, content });
  };

  // Handle edit mode
  const handleEdit = async (prompt?: Prompt) => {
    setIsEditing(true);
    if (prompt) {
      // Fetch content if not already available
      let content = prompt.content;
      if (!content) {
        content = await fetchPromptContent(prompt.name);
      }
      setEditForm({
        name: prompt.name,
        content: content || '',
      });
    } else {
      setEditForm({ name: '', content: '' });
    }
    setError('');
    setSuccess('');
  };

  // Cancel edit
  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm({ name: '', content: '' });
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  // Filter prompts based on search term
  const filteredPrompts = prompts.filter(prompt =>
    prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prompt.updated_by.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex mainWrapper w-full">
      <div className="sidenav-wrapper">
        <SideNavbar />
      </div>
      <div className="right-container">
        <div className="header-container">
          <Header />
        </div>
        <div className="body-container">
          <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-800">Prompt Management</h1>
                <button
                  onClick={() => handleEdit()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add New Prompt</span>
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Prompts List */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border">
                  <div className="p-3 border-b border-gray-200">
                    <h2 className="text-sm font-semibold text-gray-800 mb-2">Prompts</h2>
                    {/* Search Bar */}
                    <div className="relative mb-2">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="text-xs text-gray-500">
                      {filteredPrompts.length}/{prompts.length}
                    </div>
                  </div>
                  <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                    {filteredPrompts.length === 0 && !loading ? (
                      <div className="p-6 text-center text-gray-500">
                        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-xs">{searchTerm ? 'No prompts match your search' : 'No prompts available'}</p>
                      </div>
                    ) : (
                      filteredPrompts.map((prompt, index) => (
                        <div
                          key={index}
                          onClick={() => handlePromptSelect(prompt)}
                          className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${selectedPrompt?.name === prompt.name ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-800 mb-1 truncate">{prompt.name}</h3>
                              <div className="flex items-center justify-between mt-2">
                                {/* <div className="text-xs text-gray-400">
                                  Updated by: {prompt.updated_by}
                                </div> */}
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                    v{prompt.version}
                                  </span>
                                  {prompt.is_active && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                      Active
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Content Display/Edit Area */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm border">
                  {/* Status Messages */}
                  {error && (
                    <div className="mx-6 mt-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="mx-6 mt-6 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
                      {success}
                    </div>
                  )}

                  {isEditing ? (
                    // Edit Mode
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-semibold text-gray-800">
                          {editForm.name ? `Edit: ${editForm.name}` : 'Add New Prompt'}
                        </h2>
                        <div className="flex space-x-2">
                          <button
                            onClick={cancelEdit}
                            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={savePrompt}
                            disabled={saving || !editForm.name || !editForm.content}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {saving ? 'Saving...' : 'Save Prompt'}
                          </button>
                        </div>
                      </div>

                      {/* Edit Form */}
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prompt Name {selectedPrompt && <span className="text-xs text-gray-500">(read-only)</span>}
                          </label>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="e.g., response_generation_base"
                            disabled={!!selectedPrompt}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              selectedPrompt 
                                ? 'border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed' 
                                : 'border-gray-300'
                            }`}
                          />
                          {selectedPrompt && (
                            <p className="text-xs text-gray-500 mt-1">
                              Prompt name cannot be changed when editing existing prompts
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prompt Content
                          </label>
                          <textarea
                            value={editForm.content}
                            onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                            placeholder="Enter your prompt content here..."
                            rows={16}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            Characters: {editForm.content.length}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : selectedPrompt ? (
                    // View Mode
                    <div className="p-4">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h2 className="text-base font-semibold text-gray-800">{selectedPrompt.name}</h2>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-500">Version {selectedPrompt.version}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedPrompt.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                              {selectedPrompt.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setIsFullscreen(true)}
                            className="px-2 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors flex items-center space-x-1"
                            title="Fullscreen view"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            <span>Full</span>
                          </button>
                          <button
                            onClick={() => handleEdit(selectedPrompt)}
                            className="px-2 py-1.5 text-xs text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors flex items-center space-x-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                        </div>
                      </div>

                      {/* Prompt Details */}
                      <div className="bg-gray-50 p-3 rounded-lg mb-3">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                          <div>
                            <span className="font-medium text-gray-600">Created by:</span>
                            <span className="ml-1 text-gray-800">{selectedPrompt.created_by}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Updated by:</span>
                            <span className="ml-1 text-gray-800">{selectedPrompt.updated_by}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Length:</span>
                            <span className="ml-1 text-gray-800">{selectedPrompt.content?.length || 0} chars</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Lines:</span>
                            <span className="ml-1 text-gray-800">{selectedPrompt.content?.split('\n').length || 0}</span>
                          </div>
                        </div>
                      </div>

                      {/* Prompt Content */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-xs font-medium text-gray-700">
                            Prompt Content
                          </label>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span>{selectedPrompt.content?.split('\n').length || 0} lines</span>
                            <span>•</span>
                            <span>{selectedPrompt.content?.length || 0} chars</span>
                          </div>
                        </div>
                        {contentLoading ? (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-xs text-gray-500">Loading content...</p>
                          </div>
                        ) : (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-[calc(100vh-450px)] overflow-y-auto relative group">
                            <pre className="whitespace-pre-wrap font-mono text-xs text-gray-800 leading-relaxed">
                              {selectedPrompt.content || 'No content available'}
                            </pre>
                            {/* Copy button */}
                            <button
                              onClick={async () => {
                                if (selectedPrompt?.content) {
                                  try {
                                    await navigator.clipboard.writeText(selectedPrompt.content);
                                    setSuccess('Content copied to clipboard!');
                                    setTimeout(() => setSuccess(''), 2000);
                                  } catch (err) {
                                    setError('Failed to copy to clipboard. Please check permissions.');
                                    setTimeout(() => setError(''), 3000);
                                  }
                                }
                              }}
                              className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50"
                              title="Copy to clipboard"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Empty State
                    <div className="p-12 text-center">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-600 mb-2">Select a Prompt</h3>
                      <p className="text-gray-500">Choose a prompt from the list to view or edit its content.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && selectedPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">{selectedPrompt.name}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedPrompt.content?.split('\n').length || 0} lines • {selectedPrompt.content?.length || 0} characters
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={async () => {
                    if (selectedPrompt?.content) {
                      try {
                        await navigator.clipboard.writeText(selectedPrompt.content);
                        setSuccess('Content copied to clipboard!');
                        setTimeout(() => setSuccess(''), 2000);
                      } catch (err) {
                        setError('Failed to copy to clipboard. Please check permissions.');
                        setTimeout(() => setError(''), 3000);
                      }
                    }
                  }}
                  className="px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed">
                {selectedPrompt.content || 'No content available'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptManagement;